import { faWarning } from "@fortawesome/free-solid-svg-icons";
import GenericConfirmationModal from "components/modals/GenericConfirmationModal";
import { useContext, useEffect, useMemo, useState } from "react";
import ReactModal from "react-modal";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "../../../components/Spinner";
import AuthContext from "../../../context/AuthContext";
import { PageableTableProvider } from "../../../context/PageableTableContext";
import ProductContext, {
  ProductContextInterface,
} from "../../../context/ProductContext";
import { useBeforeUnload } from "../../../utils/useBeforeUnload";
import { Breadcrumb, BreadcrumbItem } from "../components/breadcrumb";
import { Hierarchy, Summary } from "../features/mpn-create";
import Composition, {
  CompositionProps,
} from "../features/mpn-create/Composition";
import { HierarchyProps } from "../features/mpn-create/Hierarchy";
import Information, {
  InformationProps,
} from "../features/mpn-create/Information";
import RequestorInfo, {
  RequestorProps,
} from "../features/mpn-create/RequestorInfo";
import { SummaryProps } from "../features/mpn-create/Summary";
import ProductPackaging from "../features/mpn-create/ProductPackaging";
import { StepsActionButtons } from "../components/stepsActionButtons";
import { useCreateMpnStore } from "../context/create/CreateMpnContext";
import { CreateMpnActionTypes, CreateMpnState } from "../states/create/data";
import { CreateMpnActions } from "../states/create";
import { HierarchyInfoSection } from "../components/hierarchy-info";
import { IPisRequest } from "../../../types/data.interface";
import { getEmptyPisRequestObject } from "../../skus/features/sku-create/sku-create.helper";
import Banner from "../../../components/Banner";
import { onSaveMpn } from "../helpers/onSaveMpn";
import useAxios from "../../../utils/useAxios";
// @ts-ignore
import { onSubmitMpn } from "../helpers/onSubmitMpn";
import { Predicates } from "../../../libraries/predicates/predicates";
import { fetchPisHistory, fetchPisRequest } from "../../../hooks/usePisRequest";
import { mpnResponseDto } from "../helpers/mpnResponseDto";
import { onApproveMpn } from "../helpers/onApproveMpn";
import SkuCreationApprovalContext, {
  SkuCreationApprovalContextInterface,
} from "../../../context/SkuCreationApprovalContext";

import { helpStepCondition } from "../helpers/helperStepConditions";
import {
  ItemNumberValuesImport,
  itemNumberValuesImportProps,
} from "../features/mpn-create/ItemNumberValuesImport";
import { onRejectMpn } from "../helpers/onRejectMpn";

const LAST_STEP_INDEX = 6;

type CreateProps = {
  step: number;
  hierarchyProps?: HierarchyProps;
  compositionProps?: CompositionProps;
  informationProps?: InformationProps;
  requestorProps?: RequestorProps;
  summaryProps?: SummaryProps;
  itemNumberValueImportProps?: itemNumberValuesImportProps;
};

const Create = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const username = authCtx?.username;
  const hasPermission = authCtx?.hasPermission;
  const hasGroup = authCtx?.hasGroup;

  const { unsavedChanges, setUnsavedChanges } =
    useContext<ProductContextInterface>(ProductContext);
  const hasApproverPermission: boolean = hasPermission(
    "product_master.approver_pis_request",
  );

  const { state, dispatch } = useCreateMpnStore();
  const location = useLocation();

  const hasApacPisApproverPermission: boolean = hasPermission(
    "product_master.approver_apac_pis_request",
  );

  const { finalizeSkuCreationProcess, sendPisRequestEmailsBasedOnAction } =
    useContext<SkuCreationApprovalContextInterface>(SkuCreationApprovalContext);


  const [searchParams, setSearchParams] = useSearchParams();
    const [isPisRequestInformationLoading, setIsPisRequestInformationLoading] =
    useState<boolean>(false);

  const {
    id,
    isApproved,
    lastApprovedRevisionNumber,
    pisRequestId,
    requestRevisionNumber,
    shouldCreate,
  } = useMemo(() => {
    return {
      id: !!searchParams.get("id")
        ? // @ts-ignore
          parseInt(searchParams.get("id"))
        : undefined,
      pisRequestId: searchParams.get("pis_request_id"),
      requestRevisionNumber: searchParams.get("revision"),
      shouldCreate: searchParams.get("should_create"),
      isApproved: searchParams.get("is_approved"),
      lastApprovedRevisionNumber: searchParams.get("last_approved_revision"),
    };
  }, [searchParams]);

  const isFinished: boolean = useMemo(
    () =>
      state.mpnRequest.status === "APPROVED" ||
      state.mpnRequest.status === "REJECTED",
    [state.mpnRequest.status],
  );

  const [step, setStep] = useState(0);

  const handleSelectItemNumber = (e: any) => {
    if (e) {
      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN, {
          ...state,
          itemNumber: {
            ...e.value,
            tcc_compositions: e?.value?.tcc_compositions?.map((c: any) => ({
              ...c,
              componentType: c?.component_type,
              pcs: c?.nr_pcs,
            })),
          },
          composition: {
            id: "",
            molded_components: [],
            name: "",
            non_molded_components: [],
            mpns: [],
            skus: [],
          },
          isChangedImportedValues: false,
          selectedItemNumberCompositionRows: [],
          selectedMoldIndexes: [],
          mpnData: {
            ...state.mpnData,
            name: e.value.name,

            stdpk: ``,
            stdpk_weight: ``,
            stdpk_length: ``,
            stdpk_width: ``,
            stdpk_height: ``,
          },
          category: e?.value?.global_product?.sub_category.parent,
          sub_category: e?.value?.global_product?.sub_category,
          product_line: e?.value?.global_product?.product_line,
          global_product: e?.value?.global_product,
        }),
      );
      return;
    }
    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_ITEM_NUMBER,
        null,
      ),
    );
  };

  const handleSelectGlobalProduct = (e: any) => {
    if (e) {
      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN, {
          ...state,
          global_product: { ...e.value },
          mpnData: {
            ...state.mpnData,
            name: e.value.name,
          },
          category: e?.value?.sub_category.parent,
          sub_category: e?.value?.sub_category,
          product_line: e?.value?.product_line,
        }),
      );
      return;
    }
    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_GLOBAL_PRODUCT,
        null,
      ),
    );
  };

  const setUnsavedChangesToTrue = () => {
    setUnsavedChanges({
      existsUnsavedChanges: true,
      message: "You may have unsaved changes. Are you sure you want to leave?",
    });
  };

  useBeforeUnload({
    condition: unsavedChanges ? unsavedChanges.existsUnsavedChanges : false,
    message: "You may have unsaved changes. Are you sure you want to leave?",
  });

  const breadCrumbsSteps = [
    "Requestor Info",
    "MPN Hierarchy",
    "Item Number Data Import",
    "MPN Composition",
    "MPN Information",
    "Product Packaging",
    "Summary",
  ];
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const hierarchyProps: any = useMemo(
    () => ({
      handleSelectItemNumber,
      handleSelectGlobalProduct,
      isFinished,
      isLoading: !!(
        location?.state?.hasOwnProperty("itemNumber") && !state?.itemNumber?.id
      ),
      setUnsavedChangesToTrue,
    }),
    [
      handleSelectItemNumber,
      handleSelectGlobalProduct,
      isFinished,
      location?.state,
      state?.itemNumber?.id,
    ],
  );

  const [pisRequest, setPisRequest] = useState<IPisRequest>(
    getEmptyPisRequestObject(username),
  );

  // const [isLoading, setIsLoading] = useState<boolean>(false)

  const retrievedPisRequest = async (
    id: any,
    requestId: any,
    revision: any,
    shouldCreate: boolean | string | null,
    isApproved: boolean | string | null,
  ) => {
    setIsLoading(true)
    const response =
      shouldCreate || isApproved
        ? await fetchPisHistory({
            id: id,
            pis_request_id: requestId,
            revision: revision,
            axios,
          })
        : await fetchPisRequest({
            id: id,
            pis_request_id: requestId,
            revision: revision,
            axios,
          });

    if (response) {
      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN, {
          ...state,
          ...mpnResponseDto(response),
        }),
      );
    }

    setIsLoading(false)
  };

  useEffect(() => {
    if (Predicates.isNotNullAndNotUndefined(id)) {
      const currentRevisionNumber: string = requestRevisionNumber
        ? requestRevisionNumber
        : "0";
      const pisRevisionNumberToRetrieve: string =
        shouldCreate &&
        Number(currentRevisionNumber) > 0 &&
        lastApprovedRevisionNumber
          ? lastApprovedRevisionNumber
          : currentRevisionNumber;

      if (currentRevisionNumber === "0" && shouldCreate) {
        // clearAllState();
        return;
      }

      setIsPisRequestInformationLoading(true);

      retrievedPisRequest(
        id,
        pisRequestId,
        pisRevisionNumberToRetrieve,
        shouldCreate,
        isApproved,
      );
    }
    //   fetch history
  }, [location]);

  useEffect(() => {
    if (location?.state?.hasOwnProperty("itemNumber")) {
      dispatch(
        CreateMpnActions.setStateValue(
          CreateMpnActionTypes.SET_GLOBAL_PRODUCT,
          location?.state?.global_product,
        ),
      );

      dispatch(
        CreateMpnActions.setStateValue(
          CreateMpnActionTypes.SET_ITEM_NUMBER,
          location?.state?.itemNumber,
        ),
      );

    } else if (location?.state?.hasOwnProperty("global_product")) {
      dispatch(
        CreateMpnActions.setStateValue(
          CreateMpnActionTypes.SET_GLOBAL_PRODUCT,
          location?.state?.global_product,
        ),
      );
      setIsLoading(false);
    }

  }, [location?.state?.itemNumber, location?.state?.global_product]);

  useEffect(() => {
    if (Number(requestRevisionNumber) > Number(state.mpnRequest.revision))
    {
       dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN_REQUEST, {
          ...state.mpnRequest,
          revision: String(requestRevisionNumber),
          status: shouldCreate ? "CREATED" : state.mpnRequest?.status
        }),
      );
    }

    if (isFinished){
      setStep(LAST_STEP_INDEX)
    }
  },[{...state.mpnRequest}])

  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);

  const moveToStep = (gotToStep: number) => {
    setStep(gotToStep);
    if (
      (gotToStep <= 2 && step > 2) &&
      (state.composition.molded_components.length > 0 ||
        state.composition.non_molded_components.length > 0)
    ) {
      setShowWarningModal(true);
    }
  };

  return (
    <>
      <ReactModal
        isOpen={showWarningModal}
        className={`custom-modal mold-comps`}
        overlayClassName="custom-overlay"
        onRequestClose={() => setShowWarningModal(false)}
      >
        <GenericConfirmationModal
          message="Any changes you make on the steps prior to step 4 - MPN Composition will result in <strong>losing the current MPN composition!</strong>"
          confirmButtonMessage="Understood"
          icon={faWarning}
          onConfirm={() => setShowWarningModal(false)}
        />
      </ReactModal>

      {isLoading && <Spinner />}

      {!isLoading && (
        <div>
          <Breadcrumb>
            {breadCrumbsSteps.map((item: any, index: number) => {
              const { isReady } = helpStepCondition(index - 1, state)

              return (
                <BreadcrumbItem
                  text={`${index + 1} - ${item}`}
                  isDisabled={
                    (isFinished && item !== LAST_STEP_INDEX) || isReady
                  }
                  onClick={() => (!isReady ? moveToStep(index) : null)}
                  showActive={step >= index}
                  isCurrent={step === index}
                />
              );
            })}
          </Breadcrumb>

          {step > 1 && step < 6 && !isFinished && <HierarchyInfoSection />}

          {step === 0 && <RequestorInfo readOnly={isFinished} />}

          {step === 1 && (
            <PageableTableProvider>
              <Hierarchy {...hierarchyProps} />
            </PageableTableProvider>
          )}

          {step === 2 && (
            <ItemNumberValuesImport
              isCompositionLoading={false}
              isInApprovalProcess={false}
              isRevision={Number(pisRequest.revision) > 0}
            />
          )}
          {step === 3 && <Composition setUnsavedChangesToTrue={() => setUnsavedChanges} />}
          {step === 4 && <Information />}
          {step === 5 && <ProductPackaging />}
          {step === 6 && <Summary />}
        </div>
      )}

      {!isFinished && !isLoading && (
        <StepsActionButtons
          step={{
            current: step,
            setStep: setStep,
            bias: [0, 6],
            isDisabled: (s: any) => helpStepCondition(s, state),
          }}
          moveToStep={(s: any) => moveToStep(s)}
          hasApproverPermission={hasApproverPermission}
          hasGroup={hasGroup}
          isFinished={isFinished}
          isSaveOrSubmitInProgress={state.isSavingOrSubmiting}
          LAST_STEP_INDEX={LAST_STEP_INDEX}
          onApprove={() =>
            onApproveMpn({
              state: state,
              axios,
              onSetValues: (
                key: CreateMpnActionTypes,
                value: Partial<CreateMpnState>,
              ) => dispatch(CreateMpnActions.setStateValue(key, value)),
              username: username,
              onSetSearchParams: (n: any, r: any) => setSearchParams(n, r),
              onNavigate: (route: string) => navigate(route),
              onFinalizeSkuCreationProcess: (
                requestId: any,
                revision: any,
                fgoods: any,
                username: any,
                pmMarket: any,
              ) =>
                finalizeSkuCreationProcess(
                  requestId,
                  revision,
                  fgoods,
                  username,
                  pmMarket,
                ),
              onSendPisRequestEmailsBasedOnAction: (
                action: string,
                requestId: any,
                revision: number,
                username: any,
                fgoods: any,
              ) =>
                sendPisRequestEmailsBasedOnAction(
                  action,
                  requestId,
                  revision,
                  username,
                  fgoods,
                ),
            })
          }
          onReject={() =>
            onRejectMpn({
              state: state,
              axios,
              setValues: (
                key: CreateMpnActionTypes,
                value: Partial<CreateMpnState>,
              ) => dispatch(CreateMpnActions.setStateValue(key, value)),
              username: username,
              setSearchParams: (n: any, r: any) => setSearchParams(n, r),
              onNavigate: (route: string) => navigate(route),
            })
          }
          onSave={() =>
            onSaveMpn({
              state: state,
              axios,
              setValues: (
                key: CreateMpnActionTypes,
                value: Partial<CreateMpnState>,
              ) => dispatch(CreateMpnActions.setStateValue(key, value)),
              username: username,
              setSearchParams: (n: any, r: any) => setSearchParams(n, r),
            })
          }
          onSubmit={() =>
            onSubmitMpn({
              state: state,
              axios,
              setValues: (
                key: CreateMpnActionTypes,
                value: Partial<CreateMpnState>,
              ) => dispatch(CreateMpnActions.setStateValue(key, value)),
              username: username,
              setSearchParams: (n: any, r: any) => setSearchParams(n, r),
              onNavigate: (route: string) => navigate(route),
            })
          }
          pisRequest={state.mpnRequest}
        />
      )}

      {!isLoading && helpStepCondition(step, state).isReady && (
        <Banner
          classname={"mt-3"}
          message={helpStepCondition(step, state).messages}
          type="danger"
        />
      )}
    </>
  );
};

export default Create;
